<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const loading = ref(true)
const content = ref('')
const pageTitle = ref('')

const slug = computed(() => route.params.slug || route.path.replace('/', ''))

async function loadPage() {
  loading.value = true
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/pages/${slug.value}`)
    content.value = response.data.content || ''
    pageTitle.value = response.data.title || slug.value
  } catch (e) {
    content.value = '<p>Page not found.</p>'
    pageTitle.value = 'Not Found'
  }
  loading.value = false
}

onMounted(loadPage)
watch(slug, loadPage)

</script>

<template>
  <div class="legal-page">
    <div class="legal-container">
      <h1 class="legal-title">{{ pageTitle }}</h1>
      
      <div v-if="loading" class="loading">Loading...</div>
      
      <div v-else class="legal-content" v-html="content"></div>
      
      <div class="legal-footer">
        <router-link to="/" class="back-link">← Back to Home</router-link>
      </div>
    </div>
  </div>
</template>

<style scoped>
.legal-page {
  min-height: 100vh;
  padding: 120px 20px 60px;
  background: #0a0a0a;
}

.legal-container {
  max-width: 800px;
  margin: 0 auto;
  background: rgba(20, 20, 20, 0.8);
  border: 1px solid #222;
  border-radius: 16px;
  padding: 3rem;
}

.legal-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #222;
}

.loading {
  text-align: center;
  color: #666;
  padding: 2rem;
}

.legal-content {
  color: #999;
  line-height: 1.8;
  -webkit-user-select: text;
  user-select: text;
  cursor: text;
}

.legal-content :deep(*) {
  -webkit-user-select: text;
  user-select: text;
}

.legal-content :deep(h1),
.legal-content :deep(h2),
.legal-content :deep(h3),
.legal-content :deep(h4),
.legal-content :deep(h5),
.legal-content :deep(h6) {
  color: #fff;
  font-weight: 600;
  margin: 2rem 0 1rem;
  line-height: 1.3;
}

.legal-content :deep(h1) { font-size: 1.6rem; }
.legal-content :deep(h2) { font-size: 1.25rem; }
.legal-content :deep(h3) { font-size: 1.1rem; }
.legal-content :deep(h4),
.legal-content :deep(h5),
.legal-content :deep(h6) { font-size: 1rem; }

.legal-content :deep(h1:first-child),
.legal-content :deep(h2:first-child),
.legal-content :deep(h3:first-child) {
  margin-top: 0;
}

.legal-content :deep(p) {
  margin-bottom: 1rem;
}

.legal-content :deep(strong),
.legal-content :deep(b) {
  color: #ccc;
  font-weight: 600;
}

.legal-content :deep(em),
.legal-content :deep(i) {
  font-style: italic;
}

.legal-content :deep(a) {
  color: var(--color-primary);
  text-decoration: underline;
}

.legal-content :deep(a:hover) {
  opacity: 0.8;
}

.legal-content :deep(ul),
.legal-content :deep(ol) {
  margin: 1rem 0;
  padding-left: 2rem;
}

/* main.css resets every site list to list-style:none for navigation/game UI.
   Legal DOCX content must restore native markers so <ol start="…"> remains
   visible and continues at the section number produced by the importer. */
.legal-content :deep(ol) {
  list-style: decimal;
}

.legal-content :deep(ul) {
  list-style: disc;
}

.legal-content :deep(li) {
  margin-bottom: 0.5rem;
}

.legal-content :deep(ul ul),
.legal-content :deep(ol ol),
.legal-content :deep(ul ol),
.legal-content :deep(ol ul) {
  margin: 0.5rem 0;
}

.legal-content :deep(ul ul) {
  list-style: circle;
}

.legal-content :deep(blockquote) {
  margin: 1rem 0;
  padding: 0.25rem 0 0.25rem 1rem;
  border-left: 3px solid #333;
  color: #777;
}

.legal-content :deep(hr) {
  border: 0;
  border-top: 1px solid #222;
  margin: 1.5rem 0;
}

.legal-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.legal-content :deep(th),
.legal-content :deep(td) {
  border: 1px solid #222;
  padding: 0.5rem 0.75rem;
  text-align: left;
}

.legal-content :deep(th) {
  color: #ccc;
  background: rgba(255, 255, 255, 0.03);
}

.legal-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.legal-footer {
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid #222;
}

.back-link {
  color: var(--color-primary);
  text-decoration: none;
  font-size: 0.9rem;
  transition: opacity 0.2s;
}

.back-link:hover {
  opacity: 0.8;
}

@media (max-width: 768px) {
  .legal-container {
    padding: 1.5rem;
  }
  
  .legal-title {
    font-size: 1.5rem;
  }
}
</style>
